import React from "react"

import { Input } from "@coval/ui"
import { Tooltip } from "@coval/ui/tooltip"
import { Info } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../styles"

interface SpectrogramPitchAnalysisConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

export const SpectrogramPitchAnalysisConfig: React.FC<
  SpectrogramPitchAnalysisConfigProps
> = ({ metricRecordData, onChange }) => {
  const handleChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value)
      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...metricRecordData.metric_metadata,
          [field]: isNaN(value) ? undefined : value,
        },
      })
    }

  const handleDataPointsPerFftSegmentChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        data_points_per_fft_segment: isNaN(value) ? undefined : value,
      },
    })
  }

  const segmentLengthSeconds =
    metricRecordData.metric_metadata?.segment_length_seconds
  const segmentLengthSecondsInvalid =
    segmentLengthSeconds !== undefined &&
    segmentLengthSeconds !== null &&
    segmentLengthSeconds <= 0

  const upperRegionPercentage =
    metricRecordData.metric_metadata?.upper_region_percentage
  const upperRegionPercentageInvalid =
    upperRegionPercentage !== undefined &&
    upperRegionPercentage !== null &&
    (upperRegionPercentage < 0 || upperRegionPercentage > 1)

  const naturalnessThreshold =
    metricRecordData.metric_metadata?.naturalness_threshold
  const naturalnessThresholdInvalid =
    naturalnessThreshold !== undefined &&
    naturalnessThreshold !== null &&
    (naturalnessThreshold < 0 || naturalnessThreshold > 1)

  const dataPointsPerFftSegment =
    metricRecordData.metric_metadata?.data_points_per_fft_segment
  const dataPointsPerFftSegmentInvalid =
    dataPointsPerFftSegment !== undefined &&
    dataPointsPerFftSegment !== null &&
    (!Number.isInteger(dataPointsPerFftSegment) || dataPointsPerFftSegment < 2)

  const minSegmentSeconds =
    metricRecordData.metric_metadata?.min_segment_seconds
  const minSegmentSecondsInvalid =
    minSegmentSeconds !== undefined &&
    minSegmentSeconds !== null &&
    minSegmentSeconds < 0

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={FIELD_CONTAINER_STYLES}>
          <div className={`${LABEL_ROW_STYLES} min-h-10`}>
            <label htmlFor="segment-length-seconds" className={LABEL_STYLES}>
              Segment Length (seconds)
            </label>
            <Tooltip content="Duration of each analysis window in seconds.">
              <button type="button" className="inline-flex" aria-label="Info">
                <Info className={INFO_ICON_STYLES} />
              </button>
            </Tooltip>
          </div>
          <Input
            id="segment-length-seconds"
            type="number"
            step="0.1"
            min="0.1"
            value={String(
              metricRecordData.metric_metadata?.segment_length_seconds ?? "",
            )}
            onChange={handleChange("segment_length_seconds")}
            placeholder="2.0"
            aria-label="Segment Length (seconds)"
          />
          {segmentLengthSecondsInvalid && (
            <p className="mt-1 text-sm text-coval-danger-text">
              Segment Length must be greater than 0 to save.
            </p>
          )}
        </div>

        <div className={FIELD_CONTAINER_STYLES}>
          <div className={`${LABEL_ROW_STYLES} min-h-10`}>
            <label htmlFor="upper-region-percentage" className={LABEL_STYLES}>
              Upper Region Percentage
            </label>
            <Tooltip content="Fraction of the frequency range to treat as the upper region (0.0–1.0).">
              <button type="button" className="inline-flex" aria-label="Info">
                <Info className={INFO_ICON_STYLES} />
              </button>
            </Tooltip>
          </div>
          <Input
            id="upper-region-percentage"
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={String(
              metricRecordData.metric_metadata?.upper_region_percentage ?? "",
            )}
            onChange={handleChange("upper_region_percentage")}
            placeholder="0.25"
            aria-label="Upper Region Percentage"
          />
          {upperRegionPercentageInvalid && (
            <p className="mt-1 text-sm text-coval-danger-text">
              Upper Region Percentage must be between 0 and 1 to save.
            </p>
          )}
        </div>

        <div className={FIELD_CONTAINER_STYLES}>
          <div className={`${LABEL_ROW_STYLES} min-h-10`}>
            <label
              htmlFor="naturalness-threshold-ratio"
              className={LABEL_STYLES}
            >
              Naturalness Threshold
            </label>
            <Tooltip content="Minimum average fill ratio (0.0–1.0) to pass.">
              <button type="button" className="inline-flex" aria-label="Info">
                <Info className={INFO_ICON_STYLES} />
              </button>
            </Tooltip>
          </div>
          <Input
            id="naturalness-threshold-ratio"
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={String(
              metricRecordData.metric_metadata?.naturalness_threshold ?? "",
            )}
            onChange={handleChange("naturalness_threshold")}
            placeholder="0.10"
            aria-label="Naturalness Threshold"
          />
          {naturalnessThresholdInvalid && (
            <p className="mt-1 text-sm text-coval-danger-text">
              Naturalness Threshold must be between 0 and 1 to save.
            </p>
          )}
        </div>

        <div className={FIELD_CONTAINER_STYLES}>
          <div className={`${LABEL_ROW_STYLES} min-h-10`}>
            <label htmlFor="noise-floor-db" className={LABEL_STYLES}>
              Noise Floor (dB)
            </label>
            <Tooltip content="dB level above which a spectrogram bin counts as filled.">
              <button type="button" className="inline-flex" aria-label="Info">
                <Info className={INFO_ICON_STYLES} />
              </button>
            </Tooltip>
          </div>
          <Input
            id="noise-floor-db"
            type="number"
            step="1"
            value={String(
              metricRecordData.metric_metadata?.noise_floor_db ?? "",
            )}
            onChange={handleChange("noise_floor_db")}
            placeholder="-15"
            aria-label="Noise Floor (dB)"
          />
        </div>

        <div className={FIELD_CONTAINER_STYLES}>
          <div className={`${LABEL_ROW_STYLES} min-h-10`}>
            <label htmlFor="FFT-window-size" className={LABEL_STYLES}>
              FFT Window Size (number of data points per FFT segment)
            </label>
            <Tooltip content="FFT window size for the spectrogram computation.">
              <button type="button" className="inline-flex" aria-label="Info">
                <Info className={INFO_ICON_STYLES} />
              </button>
            </Tooltip>
          </div>
          <Input
            id="FFT-window-size"
            type="number"
            step="1"
            min="2"
            value={String(
              metricRecordData.metric_metadata?.data_points_per_fft_segment ??
                "",
            )}
            onChange={handleDataPointsPerFftSegmentChange}
            placeholder="1024"
            aria-label="FFT Window Size (number of data points per FFT segment)"
          />
          {dataPointsPerFftSegmentInvalid && (
            <p className="mt-1 text-sm text-coval-danger-text">
              FFT Window Size must be a whole number of at least 2 to save.
            </p>
          )}
        </div>

        <div className={FIELD_CONTAINER_STYLES}>
          <div className={`${LABEL_ROW_STYLES} min-h-10`}>
            <label
              htmlFor="min-segment-length-seconds"
              className={LABEL_STYLES}
            >
              Min Segment Length (seconds)
            </label>
            <Tooltip content="Ignore trailing segments shorter than this duration.">
              <button type="button" className="inline-flex" aria-label="Info">
                <Info className={INFO_ICON_STYLES} />
              </button>
            </Tooltip>
          </div>
          <Input
            id="min-segment-length-seconds"
            type="number"
            step="0.1"
            min="0"
            value={String(
              metricRecordData.metric_metadata?.min_segment_seconds ?? "",
            )}
            onChange={handleChange("min_segment_seconds")}
            placeholder="0.5"
            aria-label="Min Segment Length (seconds)"
          />
          {minSegmentSecondsInvalid && (
            <p className="mt-1 text-sm text-coval-danger-text">
              Min Segment Length must be 0 or greater to save.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
